import { useQuery, useMutation, useQueryClient, QueryClient } from "@tanstack/react-query"
import { Api } from "@/api/worker"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { CurrentAuth, currentAuthAtom } from "@/state/auth"
import {
	ReportFormData,
	ReportSave,
	reportFormDraftAtom,
	reportFormSchema,
} from "@/state/report"
import { isAPIError } from "@/api/errors"
import axios from "axios"
import { Trip, tripsAtom, tripSchema } from "@/state/trips"
import { z } from "zod"
import React, { useState } from "react"
import { generateTrips } from "@/solver/generateTrips"
import { useToast } from "@/hooks/use-toast"
import {
	addressesCountForProfession,
	generateAddresses,
	radiusForProfession,
	uniqueAddressesDuringPeriod,
} from "@/solver/onboard-algo"
import { PlainDate } from "@/utils/date"
import { monthsBetween } from "@/utils/report"
import { QUERY_KEYS } from "./queryKeys"

export function useCreateReport() {
	const queryClient = useQueryClient()
	const { toast } = useToast()
	return useMutation({
		mutationFn: () => Api.createReport({}),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: QUERY_KEYS.report(data.id, data.key),
			})
		},
		onError: () => {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to create report draft",
			})
		},
	})
}

export function useGetReport(enabled: boolean = true) {
	const auth = useAtomValue(currentAuthAtom)
	return useQuery({
		queryKey: QUERY_KEYS.report(auth.reportId, auth.key),
		queryFn: async () => {
			const result = await Api.getReport({
				query: { key: auth.key, report_id: auth.reportId },
			})

			return result
		},
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
		retry(failureCount, error) {
			if (isAPIError(error)) {
				return error.status !== 404
			}
			return failureCount < 3
		},
		enabled: enabled && !!auth.reportId && !!auth.key,
	})
}

type ReportUrls = Pick<ReportSave, "form_url" | "pdf_url" | "trips_url">
export function useUpdateReport() {
	const auth = useAtomValue(currentAuthAtom)
	const queryClient = useQueryClient()
	const { toast } = useToast()
	return useMutation({
		mutationFn: async (params: ReportUrls) => {
			const result = await Api.updateReport({
				body: {
					key: auth.key,
					report_id: auth.reportId,
					pdf_url: params.pdf_url,
					trips_url: params.trips_url,
					form_url: params.form_url,
				},
			})

			return result
		},
		onSuccess: (_, variables) => {
			queryClient.setQueryData<ReportSave>(
				QUERY_KEYS.report(auth.reportId, auth.key),
				(prev) => {
					if (!prev) return prev
					return { ...prev, ...variables }
				}
			)
		},
		onError: () => {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to update report",
			})
		},
	})
}

export const useUpdateAndRegenerateReport = () => {
	const queryClient = useQueryClient()
	const { mutateAsync: updateReport } = useUpdateReport()
	const auth = useAtomValue(currentAuthAtom)
	const [tripsReady, setTripsReady] = useState(false)
	const [generating, setGenerating] = useState(false)
	const setTrips = useSetAtom(tripsAtom)

	const run = async (form: ReportFormData, generatedNewAddresses?: boolean) => {
		let urls: ReportUrls = {}
		try {
			setGenerating(true)
			let addresses = form.clients
			if (generatedNewAddresses) {
				const radius = radiusForProfession(form.professionType)
				const addressesCount = uniqueAddressesDuringPeriod(
					addressesCountForProfession(form.professionType),
					monthsBetween(
						PlainDate.from(form.period.range[0]),
						PlainDate.from(form.period.range[1])
					)
				)

				addresses = await generateAddresses(
					form.hqAddress,
					radius,
					addressesCount
				)
			}
			const newForm: ReportFormData = { ...form, clients: addresses }

			const trips = await generateTrips(newForm, addresses)

			const uploaded = await Api.uploadFiles({
				key: auth.key,
				report_id: auth.reportId,
				files: [
					jsonToFile(newForm, "form.json"),
					jsonToFile(trips, "trips.json"),
				],
			})
			updateReportForm(queryClient, auth, newForm)
			setTrips(trips)

			urls.form_url = uploaded.data.urls[0]
			urls.trips_url = uploaded.data.urls[1]
			urls.pdf_url = null

			setTripsReady(true)
			return trips
		} catch (e) {
			console.error("Failed to generate trips", e)
			return null
		} finally {
			await updateReport(urls)
			setGenerating(false)
		}
	}

	return { tripsReady, run, generating }
}

export function useUploadPdf() {
	const auth = useAtomValue(currentAuthAtom)
	const { mutateAsync: updateReport } = useUpdateReport()
	const { toast } = useToast()
	return useMutation({
		mutationFn: async (args: { blob: Blob; filename: string }) => {
			const file = new File([args.blob], args.filename, { type: args.blob.type })
			const data = await Api.uploadFiles({
				key: auth.key,
				report_id: auth.reportId,
				files: [file],
			})
			return data.data.urls[0]
		},
		onSuccess: (newUrl) => {
			updateReport({ pdf_url: newUrl })
		},
		onError: () => {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to update pdf",
			})
		},
	})
}

const jsonToFile = (data: object, filename: string) => {
	const blob = new Blob([JSON.stringify(data)], {
		type: "application/json",
	})
	return new File([blob], filename, { type: blob.type })
}

export function useUpdateVehicle() {
	const queryClient = useQueryClient()
	const auth = useAtomValue(currentAuthAtom)
	const { mutateAsync: updateReport } = useUpdateReport()
	const form = useAtomValue(reportFormDraftAtom)
	const { toast } = useToast()
	return useMutation({
		mutationFn: async () => {
			const file = jsonToFile(form, "form.json")
			const data = await Api.uploadFiles({
				key: auth.key,
				report_id: auth.reportId,
				files: [file],
			})
			return data.data.urls[0]
		},
		onSuccess: (newUrl) => {
			updateReport({ form_url: newUrl, pdf_url: null })
			updateReportForm(queryClient, auth, form)
		},
		onError: () => {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to update vehicle",
			})
		},
	})
}

export function useDownloadReportForm(url: string, enabled: boolean) {
	const auth = useAtomValue(currentAuthAtom)
	const setFormDraft = useSetAtom(reportFormDraftAtom)
	return useQuery({
		queryKey: QUERY_KEYS.reportForm(auth.reportId, auth.key),
		queryFn: async () => {
			const response = await axios.get<unknown>(url)
			const form = reportFormSchema.parse(response.data)
			setFormDraft(form)
			return form
		},
		enabled,
	})
}
const updateReportForm = (
	queryClient: QueryClient,
	auth: CurrentAuth,
	form: ReportFormData
) => {
	queryClient.setQueryData<ReportFormData>(
		QUERY_KEYS.reportForm(auth.reportId, auth.key),
		form
	)
}

export function useDownloadTrips(url: string, enabled: boolean) {
	const auth = useAtomValue(currentAuthAtom)
	const setTrips = useSetAtom(tripsAtom)
	return useQuery({
		queryKey: QUERY_KEYS.reportTrips(auth.reportId, auth.key),
		queryFn: async () => {
			const response = await axios.get<unknown>(url)
			// TODO: how long does this take to parse 4000 trips?
			const trips = z.array(tripSchema).parse(response.data)
			setTrips(trips)
			return trips
		},
		enabled,
	})
}
